// Sync with AI guide — the timeline toolbar action that auto-aligns element
// timing to the scene's narration (Toolbar/index.jsx, messages.syncScene.*).
// Locale-keyed like every guide (content.<lang> → { feature, title, sections }).

const syncWithAiGuide = {
  id: "sync-with-ai",
  icon: "sync",
  keywords: [
    "sync",
    "senkronize",
    "senkronizasyon",
    "sync with ai",
    "ai ile senkronize et",
    "yapay zeka ile senkronize",
    "timing",
    "zamanlama",
    "audio sync",
    "ses senkronu",
    "timeline",
    "zaman çizelgesi",
    "narration timing",
    "seslendirme zamanlaması",
    "anlatım zamanlaması",
    "scene length",
    "sahne süresi",
    "align",
    "hizala",
    "sincronizar",
    "sincronizar con ia",
    "sincronización",
    "línea de tiempo",
    "tiempos de narración",
    "duración de la escena",
  ],
  content: {
    en: {
      feature: "AI",
      title: "Sync with AI",
      sections: [
        {
          id: "overview",
          title: "Overview",
          videoUrl: null,
          text:
            "**Sync with AI** is a button with a sparkles icon in the timeline toolbar at the bottom of the editor, right after the play and speed controls — not in the AI tools group of the top navbar.\n\n" +
            "- It automatically aligns the timing of the scene's visual and text elements to its narration, so elements appear as they're mentioned.\n" +
            "- Use it to save time over manually dragging every element's timeline bar into place.",
        },
        {
          id: "how-to",
          title: "Syncing a scene",
          videoUrl: null,
          text:
            "Add narration to the scene first — Sync with AI needs audio to match the timing against.\n\n" +
            "- Click **Sync with AI** to open a short summary of what it will do: the audio file is analyzed, the timing of visual elements is aligned with the audio, and text elements are positioned according to the audio.\n" +
            "- Confirm with **Start Synchronization**, or **Cancel** to back out.\n" +
            "- While it runs, the canvas shows **Syncing scene...** and the button is disabled; **Scene synced successfully.** appears when it's done. If something goes wrong you'll see **Failed to sync scene.**\n" +
            "- Review the adjusted element bars on the timeline and fine-tune any of them by dragging — the AI sets a starting point, not a lock.",
        },
        {
          id: "what-changes",
          title: "What sync takes into account",
          videoUrl: null,
          text:
            "- Sync works with all of the scene's narration clips as they appear on the timeline — for example two speakers, or narration that starts later than the beginning of the scene.\n" +
            "- Elements are kept on screen until the narration ends.\n" +
            "- Afterwards, the scene's end marker is moved so the scene ends where its content, including the narration, ends. Looping background audio doesn't change the scene length.",
        },
      ],
    },
    tr: {
      feature: "Yapay Zeka",
      title: "AI ile Senkronize Et",
      sections: [
        {
          id: "overview",
          title: "Genel Bakış",
          videoUrl: null,
          text:
            "**AI ile Senkronize Et**, üst gezinme çubuğundaki yapay zeka araçları grubunda değil, editörün altındaki timeline araç çubuğunda, oynatma ve hız denetimlerinin hemen ardında yer alan parıltı simgeli düğmedir.\n\n" +
            "- Sahnedeki görsel ve metin öğelerinin zamanlamasını anlatımla otomatik olarak hizalar; böylece öğeler bahsedildikleri anda belirir.\n" +
            "- Her öğenin timeline çubuğunu elle sürüklemek yerine zaman kazanmak için kullanın.",
        },
        {
          id: "how-to",
          title: "Bir sahneyi senkronize etme",
          videoUrl: null,
          text:
            "Önce sahneye anlatım ekleyin — AI ile Senkronize Et, zamanlamayı eşleştirmek için bir sese ihtiyaç duyar.\n\n" +
            "- **AI ile Senkronize Et**'e tıklayarak ne yapacağının kısa özetini açın: ses dosyası analiz edilir, görsel öğelerin zamanlaması sesle hizalanır ve metin öğeleri sese göre konumlandırılır.\n" +
            "- Başlatmak için **Senkronizasyonu Başlat**'ı, vazgeçmek için **İptal**'i kullanın.\n" +
            "- İşlem sürerken tuvalde **Sahne senkronize ediliyor...** yazar ve düğme devre dışı kalır; bittiğinde **Sahne başarıyla senkronize edildi.** mesajı görünür. Bir sorun olursa **Sahne senkronize edilemedi.** uyarısını görürsünüz.\n" +
            "- Timeline'daki güncellenmiş öğe çubuklarını gözden geçirin ve gerekirse sürükleyerek ince ayar yapın — yapay zekâ bir başlangıç noktası belirler, değişmez bir kilit değil.",
        },
        {
          id: "what-changes",
          title: "Senkronizasyon neleri dikkate alır",
          videoUrl: null,
          text:
            "- Senkronizasyon, sahnedeki tüm anlatım kliplerini timeline'da göründükleri haliyle kullanır — örneğin iki konuşmacı ya da sahnenin başından daha geç başlayan bir anlatım.\n" +
            "- Öğeler, anlatım bitene kadar ekranda kalır.\n" +
            "- Ardından sahnenin bitiş işaretçisi, sahne içeriğinin (anlatım dahil) bittiği yere taşınır. Döngüyle çalan arka plan sesi sahne süresini değiştirmez.",
        },
      ],
    },
    es: {
      feature: "IA",
      title: "Sincronizar con IA",
      sections: [
        {
          id: "overview",
          title: "Descripción general",
          videoUrl: null,
          text:
            "**Sincronizar con IA** es un botón con un icono de destellos que está en la barra de herramientas de la línea de tiempo, en la parte inferior del editor, justo después de los controles de reproducción y velocidad; no está en el grupo de herramientas de IA de la barra de navegación superior.\n\n" +
            "- Alinea automáticamente los tiempos de los elementos visuales y de texto de la escena con su narración, para que cada elemento aparezca en el momento en que se menciona.\n" +
            "- Úsalo para ahorrar tiempo en lugar de arrastrar a mano la barra de cada elemento en la línea de tiempo.",
        },
        {
          id: "how-to",
          title: "Sincronizar una escena",
          videoUrl: null,
          text:
            "Primero añade narración a la escena: la sincronización necesita un audio con el que ajustar los tiempos.\n\n" +
            "- Haz clic en **Sincronizar con IA** para abrir un breve resumen de lo que hará: se analiza el archivo de audio, los tiempos de los elementos visuales se alinean con el audio y los elementos de texto se colocan según el audio.\n" +
            "- Confirma con **Iniciar sincronización** o pulsa **Cancelar** para salir.\n" +
            "- Mientras se ejecuta, el lienzo muestra **Sincronizando escena...** y el botón queda desactivado; al terminar aparece **Escena sincronizada correctamente.** Si algo sale mal, verás **No se pudo sincronizar la escena.**\n" +
            "- Revisa las barras de los elementos ajustadas en la línea de tiempo y afina cualquiera de ellas arrastrándola: la IA fija un punto de partida, no un bloqueo.",
        },
        {
          id: "what-changes",
          title: "Qué tiene en cuenta la sincronización",
          videoUrl: null,
          text:
            "- La sincronización trabaja con todos los clips de narración de la escena tal como aparecen en la línea de tiempo; por ejemplo, dos locutores o una narración que empieza más tarde que la propia escena.\n" +
            "- Los elementos permanecen en pantalla hasta que termina la narración.\n" +
            "- Después, el marcador de fin de la escena se desplaza para que la escena termine donde termina su contenido, incluida la narración. El audio de fondo en bucle no cambia la duración de la escena.",
        },
      ],
    },
  },
};

export default syncWithAiGuide;
